// react
import { Dispatch, MouseEvent, SetStateAction, useContext, useEffect, useState } from 'react';

// router
import { useParams } from 'react-router-dom';

// types
import {
	Entity,
	EntityLoanPayload,
	EntityPayload,
	EntitySubtype,
	ErrorCause,
	Feedback,
	FormField,
	Loan,
	MetadataField,
	MetadataValue,
} from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner, ContextUser, ContextUserEntity } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useEntityForms } from '../../../hooks/useEntityForms';
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';

// fields
import { DynamicField } from '../../fields/DynamicField/DynamicField';
import { FilePicker } from '../../fields/FilePicker/FilePicker';
import { Input } from '../../fields/Input/Input';
import { InputPhone } from '../../fields/InputPhone/InputPhone';
import { RadioButtons } from '../../fields/RadioButtons/RadioButtons';
import { Select } from '../../fields/Select/Select';

// ui
import { AlertFeedback, AlertFeedbackProps } from '../../ui/AlertFeedback/AlertFeedback';

// icons
import {
	PeopleOutlineOutlined as IconPeople,
	PersonOutlined as IconPerson,
} from '@mui/icons-material';

// utils
import { permissionsBeneficiary } from '../../../utils/permissions';
import { translateError } from '../../../utils/translations';

// props
export interface FormAddBeneficiaryProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading: Dispatch<SetStateAction<boolean>>;
}

export const FormAddBeneficiary = ({
	onClose,
	onSuccess,
	setIsLoading,
}: FormAddBeneficiaryProps) => {
	// hooks
	const { idEntity } = useParams();
	const { t } = useTranslation();
	const { getDefaultFields } = useEntityForms();

	// context
	const { userEntity } = useContext(ContextUserEntity);
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// vars
	const idForm = 'form-add-beneficiary';
	const isWfa = user?.role === 'WFA';

	// state
	const [beneficiaryType, setBeneficiaryType] = useState<EntitySubtype>('ORGANIZATION');
	const [entities, setEntities] = useState<Entity[]>([]);
	const [entityParentId, setEntityParentId] = useState(idEntity || userEntity?.id?.toString());
	const [feedback, setFeedback] = useState<AlertFeedbackProps | null>(null);
	const [files, setFiles] = useState<File[]>([]);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [values, setValues] = useState({
		country: String(project?.country?.id || ''),
		phone: '',
	});

	// fields state
	const fieldsDefault = getDefaultFields({ form: `BENEFICIARY_${beneficiaryType}` })?.filter(
		(field) => field.name.includes('metadata')
	);
	const [fields, setFields] = useState<FormField[]>(fieldsDefault);

	// fetch entities
	useFetch({
		isEnabled: Boolean(project && !idEntity),
		url: `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&type=INTERMEDIARY,PIU,SUB_PIU&fields=id,name`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch loans
	let fetchLoansUrl = `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=id,code,status`;

	if (isWfa && userEntity && userEntity.id) {
		fetchLoansUrl += `&entity=${userEntity.id}`;
	}

	const { fetchRequest: fetchLoans } = useFetch({
		isEnabled: Boolean(project),
		url: fetchLoansUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch form
	const { fetchRequest: fetchForm } = useFetch({
		isEnabled: Boolean(entityParentId && project),
		url: `${process.env.REACT_APP_API_URL}/forms?entity=${entityParentId}&project=${
			project?.id
		}&name=${`BENEFICIARY_${beneficiaryType}`}&limit=1&orderBy=modified&populate=user&status=PUBLISHED`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data?.[0]) {
				setFields([...fieldsDefault, ...res.data[0].fields]);
			}
		},
	});

	// options
	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: String(entity.id),
		};
	});

	const optionsLoans = loans.map((loan) => {
		return {
			disabled: loan.status === 'DISABLED',
			label: loan.code,
			value: String(loan.id),
		};
	});

	// inputs
	const radioInputs = [
		{
			icon: <IconPerson />,
			label: t('individual'),
			value: 'INDIVIDUAL',
		},
		{
			icon: <IconPeople />,
			label: t('organization'),
			value: 'ORGANIZATION',
		},
	];

	// handlers
	const handleChangeBeneficiaryType = (
		e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>,
		value: string
	) => {
		setBeneficiaryType(value as EntitySubtype);
	};

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		// set loader
		setIsLoading(true);

		// define form data
		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		const feedbackNew: AlertFeedbackProps & { items: Feedback[] } = {
			items: [],
		};

		try {
			// clear feedback
			setFeedback(null);

			// set loading
			setIsLoading(true);

			// set parent entity
			const idParent = idEntity ? Number(idEntity) : Number(entries.parent);

			// construct loans array
			const loans: EntityLoanPayload[] = [];

			const loanEntry = entries.loans as string;
			const ids = loanEntry.split(',');

			// create relationship with same parent for each loan selected
			for (let i = 0; i < ids.length; i++) {
				loans.push({
					id: Number(ids[i]),
					parent: idParent,
				});
			}

			// define body
			const body: Partial<EntityPayload> = {
				isKycVerified: Boolean(entries.isKycVerified),
				loans,
				name: String(entries[`name`] || `${entries[`user-firstName`]} ${entries[`user-lastName`]}`),
				project: project?.id,
				subtype: beneficiaryType,
				type: 'BENEFICIARY',
				users: [
					{
						country: values.country ? Number(values.country) : null,
						email: entries[`user-email`] ? String(entries[`user-email`]) : null,
						firstName: String(entries[`user-firstName`]),
						lastName: String(entries[`user-lastName`]),
						phone: values.phone ? Number(values.phone) : null,
						projects: [
							{
								id: project?.id,
								permissions: { ...permissionsBeneficiary },
							},
						],
						role: 'GENERAL',
					},
				],
			};

			const bodyMetadata = [] as MetadataField[];

			// construct body using fields and entries
			for (let i = 0; i < fields.length; i++) {
				const field = fields[i];

				let value: MetadataValue = entries[field.name] ? String(entries[field.name]) : null;

				if (field.type === 'CHECKBOX') {
					value = Boolean(value);
				}

				if (field.type === 'NUMBER') {
					value = Number(value);
				}

				let name = field.name;
				if (field.name.includes('metadata-')) {
					name = field.name.replace('metadata-', '');
				}

				bodyMetadata.push({
					isProtected: Boolean(field.isProtected),
					label: field.label,
					name,
					type: field.type,
					value,
				});
			}
			body.metadata = bodyMetadata;

			if (entries.idExternal) {
				body.idExternal = String(entries.idExternal);
			}

			if (!body.users?.[0]?.['email'] && !body.users?.[0]?.['phone']) {
				throw Error(t(`error.userEmailOrPhoneRequired`), {
					cause: {
						id: 'USER_PHONE_OR_EMAIL_EMPTY',
					},
				});
			}

			const fetchAddBeneficiary = await fetch(`${process.env.REACT_APP_API_URL}/entities`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify(body),
			});
			const resAddBeneficiary = await fetchAddBeneficiary.json();

			if (resAddBeneficiary.error) {
				throw new Error(resAddBeneficiary.error.message, {
					cause: {
						details: resAddBeneficiary.error?.details,
						id: resAddBeneficiary.error.id,
					},
				});
			}

			const beneficiary = resAddBeneficiary.data;

			// add documents using files
			for (let i = 0; i < files.length; i++) {
				// define form data
				const formData = new FormData();
				const dateToday = new Date();
				const year = dateToday.getFullYear();
				const month = new Intl.DateTimeFormat('en-US', {
					month: '2-digit',
				}).format(dateToday);

				// append form data
				formData.append(
					'path',
					`bldt/projects/${project?.id}/beneficiaries/${beneficiary.id}/${year}/${month}/`
				);
				formData.append('file', files[i]);
				formData.append('entities[]', String(beneficiary.id));

				if (project) {
					formData.append('project', String(project.id));
				}

				// POST document
				const fetchPostDoc = await fetch(`${process.env.REACT_APP_API_URL}/documents`, {
					method: 'post',
					body: formData,
					headers: {
						Authorization: `Bearer ${accessToken}`,
						User: String(idToken),
					},
				});

				const resPostDoc = await fetchPostDoc.json();

				if (resPostDoc.error) {
					feedbackNew.items.push({
						alert: {
							severity: 'error',
						},
						response: resPostDoc,
					});
				}
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			let entityName = `${entries[`user-firstName`]} ${entries[`user-lastName`]}`;

			if (beneficiaryType === 'ORGANIZATION') {
				entityName = String(entries[`name`]);
			}

			feedbackNew.items.push({
				alert: {
					children: t('alert.beneficiaryCreated', {
						name: entityName,
					}),
					severity: 'success',
				},
			});

			if (!feedbackNew.items.some((i) => i.alert.severity === 'error')) {
				setBanner({
					hasClose: true,
					message: t('alert.beneficiaryCreated', {
						name: entityName,
					}),
					severity: 'success',
				});

				// close dialog
				if (onClose) {
					onClose();
				}
			} else {
				setFeedback(feedbackNew);
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			if (cause) {
				if (cause.id === 'ENTITY_DUPLICATE' || cause.id === 'ENTITY_DUPLICATE_ID_EXTERNAL') {
					cause.details.idExternal = entries.idExternal;
				}

				if (cause.id === 'USER_DUPLICATE') {
					cause.details.email = entries[`user-email`];
					cause.details.phone = values.phone;
				}
			}

			feedbackNew.items.push({
				alert: {
					children: translateError({ cause, t, frame: 'BENEFICIARY' }),
					severity: 'error',
				},
			});

			setFeedback(feedbackNew);
		} finally {
			// set loading
			setIsLoading(false);
		}
	};

	// fetch form and loans when beneficiary type or parent entity changes
	useEffect(() => {
		if (entityParentId) {
			fetchForm();
			fetchLoans();
		}
	}, [entityParentId, beneficiaryType, fetchForm, fetchLoans]);

	return (
		<form id={idForm} name="formAddBeneficiary" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<RadioButtons
						defaultValue={beneficiaryType}
						id={`${idForm}-type`}
						inputs={radioInputs}
						label={t('type')}
						name="type"
						onChange={handleChangeBeneficiaryType}
						required={true}
					/>
				</Grid>
				{beneficiaryType === 'ORGANIZATION' && (
					<Grid item xs={12} md={6}>
						<Input id={`${idForm}-name`} label={t('name')} name="name" required={true} />
					</Grid>
				)}
				<Grid item xs={12} md={beneficiaryType === 'ORGANIZATION' ? 6 : 12}>
					<Input
						helperText={t('idExternal.helper')}
						id={`${idForm}-id-external`}
						label={t('uniqueIdentifier')}
						name="idExternal"
						required={true}
					/>
				</Grid>
				{!idEntity && Boolean(optionsEntities.length) && (
					<Grid item xs={6}>
						<Select
							id={`${idForm}-parent`}
							label={t('entity')}
							name="parent"
							onChange={(e) => {
								const { value } = e.target as HTMLInputElement;
								setEntityParentId(value);
							}}
							options={optionsEntities}
							required={true}
							value={entityParentId}
						/>
					</Grid>
				)}
				<Grid item xs={12} md={idEntity ? 12 : 6}>
					<Select
						defaultValue={[]}
						hasAllOption={true}
						id={`${idForm}-loans`}
						label={t('financingSources')}
						multiple={true}
						name="loans"
						options={optionsLoans}
						required={true}
					/>
				</Grid>
				<Grid item xs={12}>
					<Box
						sx={(theme) => {
							return {
								backgroundColor: theme.palette.brand.grey[200],
								borderRadius: 1,
								padding: theme.spacing(2),
							};
						}}>
						<Grid container spacing={2}>
							<Grid item xs={12}>
								<Typography fontWeight={600}>{t('userAccount')}</Typography>
								<Typography>{t('userAccount.intro')}</Typography>
							</Grid>

							<Grid item xs={12} lg={6}>
								<Input
									id={`${idForm}-name`}
									label={t('name')}
									name="user-firstName"
									required={true}
								/>
							</Grid>
							<Grid item xs={12} lg={6}>
								<Input id={`${idForm}-last-name`} label={t('lastName')} name="user-lastName" />
							</Grid>
							<Grid item xs={12} lg={6}>
								<Input
									helperText={t('email.helper')}
									id={`${idForm}-email`}
									label={t('email')}
									name="user-email"
								/>
							</Grid>
							<Grid item xs={12} lg={6}>
								<InputPhone
									InputProps={{
										id: `${idForm}-phone`,
										name: 'phone',
										onChange: (e) => {
											setValues((valuesPrev) => {
												return {
													...valuesPrev,
													phone: e.target.value,
												};
											});
										},
										value: values.phone,
									}}
									label={t('phone')}
									SelectProps={{
										id: `${idForm}-country`,
										name: 'country',
										onChange: (e) => {
											console.log('Value', e.target.value);
											setValues((valuesPrev) => {
												return {
													...valuesPrev,
													country: e.target.value as string,
												};
											});
										},
										value: values.country,
									}}
									variant="merged"
								/>
							</Grid>
						</Grid>
					</Box>
				</Grid>
				<Grid item xs={12}>
					<FilePicker
						onChange={(e, filesSelected) => {
							setFiles(filesSelected);
						}}
					/>
				</Grid>
				{fields.map((field, i) => {
					return (
						<Grid
							alignSelf={field.type === 'CHECKBOX' ? 'flex-end' : 'flex-start'}
							item
							key={`field-${field.name}-${i}`}
							lg={6}
							xs={12}>
							<DynamicField field={field} />
						</Grid>
					);
				})}
				<Grid item xs={12}>
					<FormControlLabel
						control={
							<Checkbox id={`${idForm}-is-kyc-verified`} name="isKycVerified" required={true} />
						}
						label={t('formAddBeneficiary.kycAml')}
					/>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<AlertFeedback {...feedback} />
					</Grid>
				)}
			</Grid>
		</form>
	);
};
